// API functions for users, permission roles, and grants.
// Follows the factory pattern established in registers.ts.

import type { ApiClient } from './client'

// --- Types ---

export interface User {
  id: string
  email: string
  display_name: string | null
  user_type: 'standard' | 'api'
  enrolment_status: 'pending' | 'active' | 'suspended'
  enrolment_method: string | null
  enrolled_at: string | null
  last_login_at: string | null
  role_count: number
  role_names: string[]
  ownership_roles: string[]
  has_api_key: boolean
  api_key_status: 'unactivated' | 'active' | 'revoked' | null
  created_at: string
}

export interface Role {
  id: string
  name: string
  description: string | null
  member_count: number
  grant_count: number
  is_composite: boolean
  created_at: string
}

export interface RoleMember {
  user_id: string
  email: string
  display_name: string | null
  user_type: 'standard' | 'api'
  assigned_at: string
  assigned_by: string | null
}

export interface Grant {
  id: string
  role_type: 'permission' | 'ownership'
  role_id: string
  role_name: string
  domain: 'register' | 'control' | 'event' | 'action' | 'framework'
  node_id: string
  node_name: string
  node_path: string
  permission_type: 'read' | 'write'
  is_system_generated: boolean
  granted_by: string | null
  granted_at: string
}

export interface UserListFilters {
  status?: string
  search?: string
  include_api?: boolean
}

export interface CreateRoleRequest {
  name: string
  description?: string
}

export interface UpdateRoleRequest {
  name?: string
  description?: string
}

export interface CreateGrantRequest {
  role_id: string
  domain: string
  node_id: string
  permission_type: string
}

export interface GrantListFilters {
  role_id?: string
  domain?: string
  node_id?: string
}

// --- API factories ---

export function usersApi(api: ApiClient) {
  return {
    list: (filters?: UserListFilters) => {
      const params = new URLSearchParams()
      if (filters?.status) params.set('enrolment_status', filters.status)
      if (filters?.search) params.set('search', filters.search)
      if (filters?.include_api) params.set('include_api', 'true')
      const qs = params.toString()
      return api.get<User[]>(`/users${qs ? `?${qs}` : ''}`)
    },
    get: (id: string) => api.get<User>(`/users/${id}`),
    create: (data: { email: string; display_name?: string }) =>
      api.post<User>('/users', data),
    suspend: (id: string) => api.post<void>(`/users/${id}/suspend`),
    reactivate: (id: string) => api.post<void>(`/users/${id}/reactivate`),
    enableApi: (id: string) => api.post<{ status: string }>(`/users/${id}/enable-api`),
    disableApi: (id: string) => api.del(`/users/${id}/api-key`),
    revokeApiKey: (id: string) => api.post<{ status: string }>(`/users/${id}/revoke-api-key`),
  }
}

export function rolesApi(api: ApiClient) {
  return {
    list: (search?: string) => {
      const qs = search ? `?search=${encodeURIComponent(search)}` : ''
      return api.get<Role[]>(`/roles${qs}`)
    },
    get: (id: string) => api.get<Role>(`/roles/${id}`),
    create: (data: CreateRoleRequest) => api.post<Role>('/roles', data),
    update: (id: string, data: UpdateRoleRequest) =>
      api.put<Role>(`/roles/${id}`, data),
    delete: (id: string) => api.del(`/roles/${id}`),
    members: (id: string) => api.get<RoleMember[]>(`/roles/${id}/members`),
    addMember: (id: string, userId: string) =>
      api.post<void>(`/roles/${id}/members`, { user_id: userId }),
    removeMember: (id: string, userId: string) =>
      api.del(`/roles/${id}/members/${userId}`),
    grants: (id: string) => api.get<Grant[]>(`/roles/${id}/grants`),
  }
}

// --- Ownership Roles ---

export interface OwnershipRole {
  id: string
  label: string
  current_owner_id: string
  owner_display_name: string
  created_at: string
}

export function ownershipRolesApi(api: ApiClient) {
  return {
    list: () => api.get<OwnershipRole[]>('/ownership-roles'),
    create: (data: { label: string }) =>
      api.post<OwnershipRole>('/ownership-roles', data),
    delete: (id: string) => api.del(`/ownership-roles/${id}`),
    transfer: (id: string, newOwnerId: string) =>
      api.post<OwnershipRole>(`/ownership-roles/${id}/transfer`, {
        new_owner_id: newOwnerId,
      }),
  }
}

export function grantsApi(api: ApiClient) {
  return {
    list: (filters?: GrantListFilters) => {
      const params = new URLSearchParams()
      if (filters?.role_id) params.set('role_id', filters.role_id)
      if (filters?.domain) params.set('domain', filters.domain)
      if (filters?.node_id) params.set('node_id', filters.node_id)
      const qs = params.toString()
      return api.get<Grant[]>(`/grants${qs ? `?${qs}` : ''}`)
    },
    create: (data: CreateGrantRequest) => api.post<Grant>('/grants', data),
    delete: (id: string) => api.del(`/grants/${id}`),
  }
}
